// The sketch itself: a draw list in, React elements out. No state, no hooks, no
// fetching — everything here is a pure function of the scene and the box it has
// to fit into, which is what makes the fit arithmetic and the escaping rules
// testable without a DOM.
//
// THERE IS NO ESCAPER BECAUSE THERE IS NO SINK, and not introducing the first
// sink is this file's most important property. Every element below is a React
// element: numbers go into geometry attributes, colours go through
// {@link asHexColor} before they are allowed near `fill` / `stroke`, and design
// text is a `<tspan>` CHILD. Deliberately absent, each for its own reason:
//
//   `dangerouslySetInnerHTML` / `innerHTML` — the sink itself. `grep` across
//       `apps/web/src` returns zero hits today and this file must not be the
//       first.
//   `<foreignObject>` — it would re-admit HTML into an SVG subtree. Text
//       wrapping is what tempts you into it; the scene ships `lines` already
//       fitted server-side precisely so that temptation never arrives.
//   `<title>` — it silently becomes both a tooltip AND the accessible name, so
//       a layer's words would be read out by a screen reader with none of the
//       border / caption / letterform boundary that tells a user this is a
//       picture of someone else's screen. The wrapper carries an `aria-label`
//       assembled from Loom's own catalog instead.
//
// Two strings from the payload DO reach attributes, and each passes a guard at
// this end as well as the server's: a vector's outline reaches `d` only after
// `isScenePath` (the path alphabet and the length cap, `@loom/shared/figma/
// scene`), and a gradient's stop colours reach `stopColor` only after
// `asHexColor`. A gradient's `id` — and the `url(#…)` that points at it — is
// Loom's own string, built from the node's INDEX and the host's `idPrefix`,
// never from anything the payload says.
//
// WHAT THE PICTURE CANNOT DO. It shows where things sit, and — where the row
// holds it — the outline a vector really has; it does not show what things
// are. A vector Loom cannot draw from its outline (a row read without
// `geometry=paths`, an outline too big to keep, or one whose placement Loom
// could not reproduce) is Loom's name-matched icon or a plain box; a picture
// has no stored bytes, so it is an empty dashed box;
// a layer switched off in the file, or too faint to see, is not drawn at all.
// Every one of those is COUNTED rather than promised away — see the caption in
// `FigmaPreview.tsx`.

import type { ReactElement } from "react";

import {
  ICON_PRIMITIVE_DEFS,
  ICON_PRIMITIVE_VIEWBOX,
  isScenePath,
  SCENE_GRADIENT_HANDLE_MAX,
  SCENE_GRADIENT_HANDLE_MIN,
  SCENE_MAX_DIMENSION,
  SCENE_MAX_GRADIENT_STOPS,
  SCENE_MAX_TRANSFORM,
  SCENE_MIN_ALPHA,
  sceneLocalBoxBounds,
  type IconPrimitiveDef,
} from "@loom/shared/figma/scene";

import {
  asHexColor,
  PREVIEW_TALL_RATIO,
  type DesignScene,
  type SceneGradient,
  type SceneNode,
} from "./useDesignScene";

export interface SceneFit {
  /** Frame px → CSS px. Never above 1: a sketch is a reduction, and upscaling
   *  a 120x60 component to fill 600px would make a rough drawing look like a
   *  claim about detail it does not have. */
  readonly scale: number;
  /** Only the top of the frame is drawn (a long scrolling page). */
  readonly cropped: boolean;
  /** How many FRAME px tall the drawn slice is (`scene.h` unless cropped). */
  readonly srcH: number;
  /** Rendered size, CSS px. */
  readonly outW: number;
  readonly outH: number;
}

export interface FitOptions {
  /** The tallest the drawing may be, CSS px. */
  readonly maxH: number;
  /** The widest it may be, CSS px — the host's measured content box. */
  readonly availW: number;
}

export interface DrawOptions extends FitOptions {
  /**
   * What this drawing's gradient ids start with — `useId()` from the host.
   *
   * A gradient is a `<linearGradient id>` a shape points at with
   * `url(#id)`, and ids are DOCUMENT-wide: the panel and the enlarged view
   * draw at the same time, and two chats can hold two sketches, so an id made
   * from the node index alone would make one drawing paint with another's
   * colours. Only letters, digits, `_` and `-` survive into the id.
   */
  readonly idPrefix?: string;
}

/**
 * Fit a frame into the host's box.
 *
 * ORDINARY frames fit height-first AND width-first at once (the smaller scale
 * wins), so the whole screen is visible. A LONG one — a scrolling page at
 * 375 x 2400 — cannot: height-first collapses it to 63px wide, which is a
 * ribbon nobody can recognise anything in. Past {@link PREVIEW_TALL_RATIO} the
 * fit switches to width-first and CROPS to the top, which is the part a person
 * recognises a screen by. The crop is never silent: `cropped` drives both a
 * dashed cut edge across the bottom and a sentence under the drawing.
 */
export const fitScene = (
  scene: Pick<DesignScene, "w" | "h">,
  { maxH, availW }: FitOptions,
): SceneFit => {
  const w = Math.max(1, scene.w);
  const h = Math.max(1, scene.h);
  const width = Math.max(1, availW);
  const height = Math.max(1, maxH);
  const tall = h / w > PREVIEW_TALL_RATIO;
  const scale = tall
    ? Math.min(width / w, 1)
    : Math.min(width / w, height / h, 1);
  const srcH = tall ? Math.min(h, height / scale) : h;
  return {
    scale,
    cropped: srcH < h - 0.5,
    srcH,
    outW: w * scale,
    outH: srcH * scale,
  };
};

/**
 * The icon table is `@loom/shared`'s, NOT a copy of it.
 *
 * `ICON_PRIMITIVES` is a CLOSED enum the server matches layer names against,
 * and the shapes live beside it (`@loom/shared/figma/scene`) precisely so this
 * file does not carry a second copy. A second copy drifts the first time a
 * primitive is added, and the drift is INVISIBLE — an id this build does not
 * know simply draws a plain box, so a status bar quietly loses its wifi,
 * battery and signal glyphs with every test on both sides still green. That
 * is not hypothetical: it is what the two halves of this feature did to each
 * other before they were merged.
 *
 * The subpath is the contract-only module: it imports zod and nothing else, so
 * this costs the web bundle the path strings and never the 2,900-line
 * projector.
 */
const iconDef = (id: string): IconPrimitiveDef | null => {
  // An OWN-property check, never a bare index. `ICON_PRIMITIVE_DEFS` is a plain
  // object, so `defs["constructor"]`, `defs["toString"]` and `defs["__proto__"]`
  // each return a PROTOTYPE member instead of `undefined` — and a function
  // shaped nothing like a def then renders as `<g><path/></g>`, an invisible
  // node, rather than falling through to the plain box this comment promises.
  // Unreachable from a server scene (the wire enum is closed) and deliberately
  // reachable from this mirror, which is permissive on purpose — which is
  // exactly the deploy-skew case the table sharing is about.
  //
  // `hasOwnProperty.call`, not `Object.hasOwn`: this bundle targets safari14
  // (`apps/web/vite.config.ts`), where `Object.hasOwn` does not exist — and
  // vite's `target` rewrites SYNTAX, never built-ins, so it would throw at
  // runtime with every test still green. Same call shape `useWsSession.ts` and
  // `figmaCopy.ts` already use here.
  if (!Object.prototype.hasOwnProperty.call(ICON_PRIMITIVE_DEFS, id)) {
    return null;
  }
  return (
    (ICON_PRIMITIVE_DEFS as Readonly<Record<string, IconPrimitiveDef>>)[id] ??
    null
  );
};

// The ink for a shape the design gave no paint. Chosen against the WHITE paper
// the drawing sits on (`--figma-paper`, focus.css), which is pinned white in
// both themes — so this is a literal, not a theme token. Nothing in this file
// is theme-adjusted: the design's own colours are someone else's, and a dark
// Loom must never recolour them (no filter, no invert).
const FALLBACK_INK = "#9aa0aa";

/** A geometry number that is safe to put in an attribute. NaN / Infinity from a
 *  corrupt blob would otherwise reach the DOM as the literal string "NaN", and
 *  an SVG with a NaN in a `d` attribute drops the whole path silently. */
const num = (value: number | undefined, fallback = 0): number =>
  typeof value === "number" && Number.isFinite(value) ? value : fallback;

/**
 * `op` as the attribute takes it: `null` when fully opaque (no attribute), the
 * TRUE value otherwise.
 *
 * There used to be a 0.05 floor here, and it is how the sketch invented a
 * shape: the real design paints its status bar's background at alpha 0.0001,
 * the projector passed that on as `op: 0`, and the floor turned it into a
 * visible grey band across a white screen. Below `SCENE_MIN_ALPHA` the node
 * is not drawn at all — see {@link drawNode}.
 */
const clamp01 = (value: number | undefined): number | null => {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  if (value >= 1) return null; // fully opaque — no attribute needed
  return Math.max(0, value);
};

/** A number for a `transform` string: four decimals, and never `-0`. */
const fmt = (value: number): string => {
  const r = Math.round(value * 1e4) / 1e4;
  return String(r === 0 ? 0 : r);
};

const inRange = (value: unknown, lo: number, hi: number): value is number =>
  typeof value === "number" && Number.isFinite(value) && value >= lo && value <= hi;

/** What one node draws: the element, and the gradient it paints with, if
 *  any. The gradient goes into the drawing's one `<defs>`. */
interface Drawn {
  readonly el: ReactElement;
  readonly def?: ReactElement;
}

/**
 * A payload gradient → a `<linearGradient>` / `<radialGradient>`, or `null`
 * when any part of it fails its re-check (the shape then paints with no fill,
 * never with a colour this file made up).
 *
 * `space` is the outline's local box when the gradient paints a `path`: there
 * the gradient is laid out in `userSpaceOnUse` — the node's own local units,
 * because it is referenced from inside the node's `<g transform>` — so it
 * spans the node's real box and turns with it. Every other shape is placed by
 * its axis-aligned box, and takes `objectBoundingBox`, where Figma's
 * normalised handles are already the right numbers.
 */
const gradientDef = (
  grad: SceneGradient | undefined,
  id: string,
  space: { readonly sw: number; readonly sh: number } | null,
): ReactElement | null => {
  if (grad === undefined) return null;
  if (grad.k !== "l" && grad.k !== "r") return null;
  const p = grad.p;
  if (
    !Array.isArray(p) ||
    p.length !== 4 ||
    !p.every((v) => inRange(v, SCENE_GRADIENT_HANDLE_MIN, SCENE_GRADIENT_HANDLE_MAX))
  )
    return null;
  const stops = grad.s;
  if (
    !Array.isArray(stops) ||
    stops.length < 2 ||
    stops.length > SCENE_MAX_GRADIENT_STOPS
  )
    return null;
  const clean: Array<{ o: number; c: string; a?: number }> = [];
  let prev = 0;
  for (const stop of stops) {
    const c = asHexColor(stop.c);
    if (c === null || !inRange(stop.o, 0, 1) || stop.o < prev) return null;
    if (stop.a !== undefined && !inRange(stop.a, 0, 1)) return null;
    prev = stop.o;
    clean.push({ o: stop.o, c, ...(stop.a !== undefined ? { a: stop.a } : {}) });
  }
  const [x1, y1, x2, y2] = p as [number, number, number, number];
  const sx = space?.sw ?? 1;
  const sy = space?.sh ?? 1;
  const units = space !== null ? "userSpaceOnUse" : "objectBoundingBox";
  const kids = clean.map((stop, j) => (
    <stop
      key={j}
      offset={stop.o}
      stopColor={stop.c}
      {...(stop.a !== undefined && stop.a < 1 ? { stopOpacity: stop.a } : {})}
    />
  ));
  if (grad.k === "l") {
    return (
      <linearGradient
        key={id}
        id={id}
        gradientUnits={units}
        x1={x1 * sx}
        y1={y1 * sy}
        x2={x2 * sx}
        y2={y2 * sy}
      >
        {kids}
      </linearGradient>
    );
  }
  // Figma's radial handles are the centre and the end of the first axis; the
  // distance between them is the radius. In `objectBoundingBox` units that
  // circle stretches with the box, which is what Figma's normalised space does
  // too.
  const r = Math.hypot((x2 - x1) * sx, (y2 - y1) * sy);
  if (!(r > 0)) return null;
  return (
    <radialGradient
      key={id}
      id={id}
      gradientUnits={units}
      cx={x1 * sx}
      cy={y1 * sy}
      r={r}
    >
      {kids}
    </radialGradient>
  );
};

/**
 * A `path` node → its outline, placed in the node's box. `null` when ANY part
 * fails its re-check, and the caller then draws the plain box — the same
 * honest fallback the projector would have chosen.
 *
 * THE PLACEMENT is arithmetic on two things the payload vouches for: the box
 * Loom placed (`x`/`y`, or the unclipped `bx`/`by` for a shape running off the
 * frame's left or top edge) and the linear matrix `m`. The local box's four
 * corners are mapped by `m` (`sceneLocalBoxBounds`), and the local origin sits
 * at the box's top-left minus the smallest of them — so the outline lands
 * exactly in the box Figma reported, without trusting a translation from the
 * payload.
 */
const drawPath = (
  node: SceneNode,
  key: string,
  gradId: string,
  fill: string | null,
  stroke: string | null,
  dim: { opacity?: number },
): Drawn | null => {
  const d = node.d;
  const sd = node.sd;
  if (d !== undefined && !isScenePath(d)) return null;
  if (sd !== undefined && !isScenePath(sd)) return null;
  const showD = d !== undefined;
  const showSd = sd !== undefined && stroke !== null;
  if (!showD && !showSd) return null;

  const m = node.m ?? [1, 0, 0, 1];
  if (
    m.length !== 4 ||
    !m.every((v) => inRange(v, -SCENE_MAX_TRANSFORM, SCENE_MAX_TRANSFORM))
  )
    return null;
  const [a, b, c, dd] = m as [number, number, number, number];
  if (Math.abs(a * dd - b * c) < 1e-6) return null;
  const sw = node.sw ?? (node.m === undefined ? node.w : undefined);
  const sh = node.sh ?? (node.m === undefined ? node.h : undefined);
  if (
    !inRange(sw, 0, SCENE_MAX_DIMENSION) ||
    !inRange(sh, 0, SCENE_MAX_DIMENSION) ||
    (sw <= 0 && sh <= 0)
  )
    return null;

  const bounds = sceneLocalBoxBounds([a, b, c, dd], sw, sh);
  const px = inRange(node.bx, -SCENE_MAX_DIMENSION, SCENE_MAX_DIMENSION)
    ? node.bx
    : num(node.x);
  const py = inRange(node.by, -SCENE_MAX_DIMENSION, SCENE_MAX_DIMENSION)
    ? node.by
    : num(node.y);
  const ox = px - bounds.minX;
  const oy = py - bounds.minY;

  const def =
    showD && fill === null ? gradientDef(node.grad, gradId, { sw, sh }) : null;
  const dFill = fill ?? (def !== null ? `url(#${gradId})` : "none");
  return {
    el: (
      <g
        key={key}
        transform={`matrix(${fmt(a)} ${fmt(b)} ${fmt(c)} ${fmt(dd)} ${fmt(ox)} ${fmt(oy)})`}
        {...dim}
      >
        {showD && (
          <path
            d={d}
            fill={dFill}
            fillRule={node.dr === "evenodd" ? "evenodd" : "nonzero"}
          />
        )}
        {showSd && (
          // The stroke's OUTLINE, as a region: its weight, caps and joins are
          // already in the shape, so it is filled and never stroked.
          <path
            d={sd}
            fill={stroke}
            fillRule={node.sdr === "evenodd" ? "evenodd" : "nonzero"}
          />
        )}
      </g>
    ),
    ...(def !== null ? { def } : {}),
  };
};

/**
 * One scene node → one SVG element (and the gradient it paints with).
 *
 * Returns `null` for anything it cannot draw honestly (zero area, an unknown
 * shape word, an opacity too faint to see), which is a smaller lie than
 * drawing a guess.
 */
const drawNode = (
  node: SceneNode,
  key: string,
  gradId: string,
): Drawn | null => {
  const x = num(node.x);
  const y = num(node.y);
  const w = num(node.w);
  const h = num(node.h);
  const fill = asHexColor(node.fill);
  const stroke = asHexColor(node.stroke);
  const opacity = clamp01(node.op);
  // Too faint to see is not drawn. `op: 0` and `op: 0.0001` both reach here
  // from a scene this file did not project; drawing them at any visible
  // opacity is a shape the design does not have.
  if (opacity !== null && opacity < SCENE_MIN_ALPHA) return null;
  const rot = num(node.rot);
  // The bounding box is all a scene carries, so a rotated node turns about its
  // own centre — the least-wrong choice available without the node's origin.
  const spin =
    rot !== 0
      ? {
          transform: `rotate(${((rot * 180) / Math.PI).toFixed(2)} ${(x + w / 2).toFixed(2)} ${(y + h / 2).toFixed(2)})`,
        }
      : {};
  const dim = opacity !== null ? { opacity } : {};

  switch (node.kind) {
    case "text": {
      const lines = (node.lines ?? []).filter((l) => l !== "");
      if (lines.length === 0) return null;
      const size = Math.max(1, num(node.size, 12));
      const lh = Math.max(size, num(node.lh, size * 1.3));
      const align = node.align ?? "left";
      const anchorX = align === "center" ? x + w / 2 : align === "right" ? x + w : x;
      const anchor =
        align === "center" ? "middle" : align === "right" ? "end" : "start";
      // First baseline sits an ascent below the box top, with the line-box
      // leading split above it — the same vertical rhythm a browser gives a
      // block of text, which is what makes the sketch line up with the frame.
      const first = y + (lh - size) / 2 + size * 0.8;
      const weight = num(node.weight, 400);
      const track = num(node.track);
      const def = fill === null ? gradientDef(node.grad, gradId, null) : null;
      const el = (
        <text
          key={key}
          x={anchorX}
          y={first}
          fill={fill ?? (def !== null ? `url(#${gradId})` : FALLBACK_INK)}
          fontSize={size}
          fontWeight={weight > 0 ? weight : 400}
          textAnchor={anchor}
          {...(track !== 0 ? { letterSpacing: track } : {})}
          {...spin}
          {...dim}
        >
          {lines.map((line, i) => (
            // `x` on EVERY tspan: `dy` alone moves the baseline down but leaves
            // the pen where the previous line ended, so a second line without it
            // starts at the end of the first.
            <tspan key={`${key}-l${i}`} x={anchorX} {...(i === 0 ? {} : { dy: lh })}>
              {line}
            </tspan>
          ))}
        </text>
      );
      return { el, ...(def !== null ? { def } : {}) };
    }
    case "line": {
      // A divider. The real design has one whose bounding box is 375x0 and
      // whose render bounds are 374x1 — so its height is not what makes it
      // visible, a minimum stroke is.
      const mid = y + h / 2;
      return {
        el: (
          <line
            key={key}
            x1={x}
            y1={mid}
            x2={x + w}
            y2={mid}
            stroke={stroke ?? fill ?? FALLBACK_INK}
            strokeWidth={Math.max(1, h)}
            {...spin}
            {...dim}
          />
        ),
      };
    }
    case "image": {
      if (w <= 0 || h <= 0) return null;
      // No `imageRef` bytes are stored, so there is nothing to draw INSIDE it.
      // A dashed empty box says "a picture belongs here and Loom does not have
      // it"; a grey filled box would say "the design is grey here". A ROUND
      // picture — the projector sends `radius: min(w,h)/2` for an `ELLIPSE`
      // with an image fill, the avatar every profile screen has — is a dashed
      // ellipse, because a square placeholder where a circle sits is a shape
      // the design does not have.
      const radius = num(node.radius);
      const dashed = {
        fill: "none",
        stroke: stroke ?? FALLBACK_INK,
        strokeWidth: 1,
        strokeDasharray: "4 3",
      };
      if (radius > 0 && radius >= Math.min(w, h) / 2 - 0.05) {
        return {
          el: (
            <ellipse
              key={key}
              cx={x + w / 2}
              cy={y + h / 2}
              rx={w / 2}
              ry={h / 2}
              {...dashed}
              {...spin}
              {...dim}
            />
          ),
        };
      }
      return {
        el: (
          <rect
            key={key}
            x={x}
            y={y}
            width={w}
            height={h}
            rx={radius || undefined}
            {...dashed}
            {...spin}
            {...dim}
          />
        ),
      };
    }
    case "ellipse": {
      if (w <= 0 || h <= 0) return null;
      const def = fill === null ? gradientDef(node.grad, gradId, null) : null;
      return {
        el: (
          <ellipse
            key={key}
            cx={x + w / 2}
            cy={y + h / 2}
            rx={w / 2}
            ry={h / 2}
            fill={fill ?? (def !== null ? `url(#${gradId})` : "none")}
            {...(stroke !== null ? { stroke, strokeWidth: 1 } : {})}
            {...spin}
            {...dim}
          />
        ),
        ...(def !== null ? { def } : {}),
      };
    }
    case "path": {
      const drawn = drawPath(node, key, gradId, fill, stroke, dim);
      if (drawn !== null) return drawn;
      // An outline this build will not draw — outside the path alphabet, over
      // the cap, a matrix out of bounds — is the plain box, exactly what the
      // projector draws when it holds no outline at all.
      break;
    }
    case "icon": {
      if (w <= 0 || h <= 0) return null;
      const def = node.icon === undefined ? null : iconDef(node.icon);
      // An id this build does not know is NOT drawn as a guess — it falls
      // through to the same plain box an unmatched vector gets, which the
      // caption already counts. With the table shared this is now only
      // reachable across a deploy skew, which is exactly when a guess would be
      // worst.
      if (def === null) break;
      // The id says the STATE, the table says the shape: `star-filled` is the
      // one primitive that paints rather than strokes, and four painted stars
      // beside one outline is the single most useful thing a sketch can say
      // about a rating row.
      const filled = def.filled === true;
      const ink = fill ?? stroke ?? FALLBACK_INK;
      return {
        el: (
          <g
            key={key}
            transform={`translate(${x.toFixed(2)} ${y.toFixed(2)}) scale(${(w / ICON_PRIMITIVE_VIEWBOX).toFixed(4)} ${(h / ICON_PRIMITIVE_VIEWBOX).toFixed(4)})`}
            fill={filled ? ink : "none"}
            stroke={ink}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            {...dim}
          >
            <path d={def.d} />
          </g>
        ),
      };
    }
    case "box":
      break;
    default:
      // A shape word from a newer projector. One node lost, never the frame.
      return null;
  }
  if (w <= 0 || h <= 0) return null;
  const def = fill === null ? gradientDef(node.grad, gradId, null) : null;
  return {
    el: (
      <rect
        key={key}
        x={x}
        y={y}
        width={w}
        height={h}
        rx={num(node.radius) || undefined}
        fill={fill ?? (def !== null ? `url(#${gradId})` : "none")}
        {...(stroke !== null ? { stroke, strokeWidth: 1 } : {})}
        {...spin}
        {...dim}
      />
    ),
    ...(def !== null ? { def } : {}),
  };
};

/** The host's `idPrefix`, reduced to characters an id and a `url(#…)` both
 *  take without escaping. */
const idPrefixOf = (raw: string | undefined): string =>
  `fig${(raw ?? "").replace(/[^A-Za-z0-9_-]/g, "")}`;

export interface DrawnScene {
  readonly svg: ReactElement;
  readonly fit: SceneFit;
}

/**
 * The whole drawing.
 *
 * The `<svg>` is `aria-hidden` and `pointer-events: none`: nothing inside it is
 * focusable, nothing inside it is clickable, and its accessible name is the
 * wrapper's, built from Loom's own catalog. A sentence in someone's design can
 * therefore be READ on this surface but cannot be ACTED on — which is the only
 * half of chrome imitation a renderer can close.
 */
export const drawScene = (
  scene: DesignScene,
  opts: DrawOptions,
): DrawnScene => {
  const fit = fitScene(scene, opts);
  const prefix = idPrefixOf(opts.idPrefix);
  const nodes: ReactElement[] = [];
  const defs: ReactElement[] = [];
  scene.nodes.forEach((node, i) => {
    // A cropped frame draws only what intersects the visible slice. The nodes
    // are already clamped to the frame server-side; this is the crop, which the
    // server knows nothing about because it depends on the host's box.
    if (fit.cropped && num(node.y) >= fit.srcH) return;
    const drawn = drawNode(node, `n${i}`, `${prefix}g${i}`);
    if (drawn === null) return;
    nodes.push(drawn.el);
    if (drawn.def !== undefined) defs.push(drawn.def);
  });
  const svg = (
    <svg
      className="fig-prev-svg"
      width={fit.outW}
      height={fit.outH}
      viewBox={`0 0 ${scene.w} ${fit.srcH}`}
      role="presentation"
      aria-hidden="true"
      focusable="false"
      shapeRendering="geometricPrecision"
    >
      {defs.length > 0 && <defs>{defs}</defs>}
      {nodes}
      {fit.cropped && (
        // The cut edge. A drawing that simply stopped would read as "the design
        // ends here", which is the false void this whole feature is designed
        // against; the dashed rule says the page carries on.
        <line
          x1={0}
          y1={fit.srcH - 0.75 / Math.max(fit.scale, 0.01)}
          x2={scene.w}
          y2={fit.srcH - 0.75 / Math.max(fit.scale, 0.01)}
          className="fig-prev-cut"
          strokeWidth={1.5 / Math.max(fit.scale, 0.01)}
          strokeDasharray={`${6 / Math.max(fit.scale, 0.01)} ${4 / Math.max(fit.scale, 0.01)}`}
        />
      )}
    </svg>
  );
  return { svg, fit };
};
